import { useId, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { RwCard, RwCardContent } from '@/common/presentation/components/rw';
import type { LibraryOverflowAction } from './LibraryOverflowMenu';
import { LibraryCardMeta } from './LibraryCardMeta';
import { LibraryCardOpenTarget } from './LibraryCardOpenTarget';
import { LibraryPreviewFrame, type LibraryPreviewFit } from './LibraryPreviewFrame';
import { LIBRARY_CARD_OPEN_SLOT, LIBRARY_CARD_OVERFLOW_SLOT, LibraryCardHoverControls } from './LibraryCardHoverControls';

const SLOTS = `:is([data-slot="${LIBRARY_CARD_OVERFLOW_SLOT}"], [data-slot="${LIBRARY_CARD_OPEN_SLOT}"])`;
const REVEALED = { opacity: 1, pointerEvents: 'auto', '& *': { pointerEvents: 'auto' } } as const;

/** Hover/focus swaps the preview window onto the full-page placement. The window owns the transition. */
const PREVIEW_OPEN = {
  height: 'var(--lp-h-open)',
  '--lp-scale': 'var(--lp-scale-open)',
  '--lp-x': 'var(--lp-x-open)',
  '--lp-y': 'var(--lp-y-open)'
} as const;

export interface LibraryCardProps {
  name: string;
  /**
   * Real destination — a native `href` so middle-click / "open in new tab" still work. Omit it for a
   * card that has no page to open (e.g. an archived template): the full-card target is then a
   * button that runs `onOpen`.
   */
  to?: string;
  onOpen: () => void;
  /** Accessible name of the full-card target; defaults to "Open {name}". */
  openLabel?: string;
  /** The hero preview — rendered inside a `LibraryPreviewFrame`. */
  preview: ReactNode;
  previewFit?: LibraryPreviewFit;
  /** Kind/category eyebrow — blocks only (a `LibraryCardKicker`). */
  kicker?: ReactNode;
  /** Status lozenge. */
  status: ReactNode;
  version: string;
  editedLabel: string;
  extraMeta?: string[];
  actions: LibraryOverflowAction[];
  actionsLabel: string;
  actionsTestId?: string;
  testId?: string;
  openTestId?: string;
}

/**
 * One library tile: a white peer surface on the grey gallery band (Figma `bl.library.default`
 * node `6:3`, `tl.library.default` node `5:3`) — hero preview, optional kicker, title, then a
 * status/version/edited meta line pinned to the card's foot.
 *
 * An invisible full-card link carries the card's name as its accessible name and both click and
 * Enter/Space (native `<a>` semantics), so middle-click / "open in new tab" keep working. A card
 * without a `to` (nothing to open, e.g. an archived template) gets an invisible full-card button in
 * the same place, running `onOpen` under `openLabel`. Layered above it (siblings, never nested) are
 * the designer-playground hover controls — a ghost ⋮ overflow top-right and a primary arrow
 * bottom-right that also runs `onOpen`. Both are revealed on `:hover`/`:focus-within`, stay up while
 * the menu is open, and stay visible on touch (`@media (hover: none)`), per design ruling
 * 2026-09-07: "the overflow should appear only on hover, it shouldn't be there all the time".
 * The arrow only duplicates the full-card link for pointer users, so it is out of the tab order and
 * the accessibility tree: one tab stop per action, no two controls with the same name. The card title
 * is an `h2` (the page's `h1` is the only level above it).
 */
export function LibraryCard({
  name,
  to,
  onOpen,
  openLabel,
  preview,
  previewFit,
  kicker,
  status,
  version,
  editedLabel,
  extraMeta = [],
  actions,
  actionsLabel,
  actionsTestId,
  testId,
  openTestId
}: LibraryCardProps) {
  const resolvedOpenLabel = openLabel ?? `Open ${name}`;
  // The status lozenge, version and edited date are read with the card's open target, so a keyboard or
  // screen-reader user hears "Open X, Published, v1, Edited ..." instead of only the name.
  const metaId = useId();

  return (
    <Box component="article" data-library-card="" data-testid={testId} sx={{ height: '100%', width: '100%', minWidth: 0 }}>
      <RwCard
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          [`&:hover ${SLOTS}, &:focus-within ${SLOTS}, &:has([aria-expanded="true"]) ${SLOTS}`]: REVEALED,
          // `& ` matters: without it stylis attaches `:is(...)` to the card root itself, so the slots
          // (its descendants) never match and stay invisible on a touch device.
          '@media (hover: none)': { [`& ${SLOTS}`]: REVEALED },
          '&:hover [data-preview-window], &:focus-within [data-preview-window]': PREVIEW_OPEN,
          '@media (prefers-reduced-motion: reduce)': {
            '& [data-preview-window], & [data-preview-sheet]': { transition: 'none' }
          }
        }}
      >
        {/* The single open target — first in DOM/tab order, so Tab reaches the card's primary
            action (open) before its secondary one (overflow), even though the overflow paints
            visually above it (zIndex 2 > 1) so it stays independently clickable. */}
        <LibraryCardOpenTarget to={to} onOpen={onOpen} label={resolvedOpenLabel} describedById={metaId} testId={openTestId} />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            // The crop paints above the full-card link. `pointer-events: none` does not
            // inherit, so every descendant is opted out too; the hover slots set `auto`
            // (and their children) once the card reveals them.
            pointerEvents: 'none',
            '& :where(*)': { pointerEvents: 'none' }
          }}
        >
          <LibraryPreviewFrame
            hoverExpand
            fit={previewFit}
            testId={testId ? `${testId}:preview` : undefined}
            overlay={
              <LibraryCardHoverControls
                openLabel={resolvedOpenLabel}
                onOpen={onOpen}
                actions={actions}
                actionsLabel={actionsLabel}
                actionsTestId={actionsTestId}
                openTestId={testId ? `${testId}:open-arrow` : undefined}
              />
            }
          >
            {preview}
          </LibraryPreviewFrame>
        </Box>

        <RwCardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 'var(--size-8)' }}>
          {kicker}
          <Typography
            component="h2"
            sx={{
              m: 0,
              color: 'var(--text-heading)',
              fontSize: 'var(--font-heading-5-size, 1.125rem)',
              fontWeight: 'var(--font-weight-semibold, 600)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word'
            }}
          >
            {name}
          </Typography>
          <LibraryCardMeta id={metaId} status={status} version={version} editedLabel={editedLabel} extraMeta={extraMeta} />
        </RwCardContent>
      </RwCard>
    </Box>
  );
}
